<template>
  <div class="modal fade" id="activeCertModal" tabindex="-1" role="dialog" aria-labelledby="activeCertModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header border-0 pb-0">
          <div class="modal-header-content w-100">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div class="modal-icon me-3">
                  <i class="bx bx-badge-check"></i>
                </div>
                <div>
                  <h5 class="modal-title mb-0 fw-bold text-white">{{ application.licenceBillNo }}</h5>
                  <p class="text-white-50 mb-0 small text-white">{{ application.businessName }}</p>
                </div>
              </div>
              <div class="d-flex align-items-center gap-2">
                <span class="badge bg-success px-3 py-2">{{ application.status }}</span>
                <span v-if="application.approved === 'true'" class="badge bg-info px-3 py-2">
                  <i class="bx bx-check me-1"></i>Permit Ready
                </span>
                <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Application Timeline -->
          <div class="timeline-section mb-4" v-if="applicationStatuses.length > 0">
            <h6 class="section-title mb-3">
              <i class="bx bx-git-branch me-2"></i>Application Timeline
            </h6>
            <div class="timeline-track">
              <div 
                v-for="(item, index) in applicationStatuses" 
                :key="index" 
                class="timeline-item"
                :class="getTimelineClass(item)">
                <div class="timeline-marker">
                  <i v-if="item.completed === 'true'" class="bx bx-check"></i>
                  <i v-else-if="item.completed === 'active'" class="bx bx-loader-alt bx-spin"></i>
                  <span v-else>{{ index + 1 }}</span>
                </div>
                <div class="timeline-content">
                  <span class="timeline-status">{{ item.status }}</span>
                  <span class="timeline-date">{{ formatDate(item.updatedDate) }}</span>
                  <span class="timeline-officer" v-if="item.issuingOfficer">{{ item.issuingOfficer }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Info Cards Grid -->
          <div class="row g-3">
            <!-- Business Details -->
            <div class="col-md-6">
              <div class="info-card">
                <div class="info-card-header">
                  <i class="bx bx-building"></i>
                  <span>Business Details</span>
                </div>
                <div class="info-card-body">
                  <div class="info-row">
                    <span class="info-label">Business Name</span>
                    <span class="info-value fw-semibold">{{ application.businessName }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Business ID</span>
                    <span class="info-value">{{ application.businessID }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Category</span>
                    <span class="info-value">{{ application.businessCategory }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Sub Category</span>
                    <span class="info-value">{{ application.businessSubCategory }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Location Details -->
            <div class="col-md-6">
              <div class="info-card">
                <div class="info-card-header">
                  <i class="bx bx-map"></i>
                  <span>Location Details</span>
                </div>
                <div class="info-card-body">
                  <div class="info-row">
                    <span class="info-label">Sub County</span>
                    <span class="info-value">{{ application.subCountyName }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Ward</span>
                    <span class="info-value">{{ application.wardName }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Plot Number</span>
                    <span class="info-value">{{ application.plotNumber }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Fiscal Year</span>
                    <span class="info-value">{{ application.fiscalYear }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Licence Details -->
            <div class="col-md-6">
              <div class="info-card">
                <div class="info-card-header">
                  <i class="bx bx-id-card"></i>
                  <span>Licence Details</span>
                </div>
                <div class="info-card-body">
                  <div class="info-row">
                    <span class="info-label">Date Inspected</span>
                    <span class="info-value">{{ formatDate(application.dateInspected) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Licence Category</span>
                    <span class="info-value">{{ application.licenceCategory }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Licence Sub Category</span>
                    <span class="info-value">{{ application.licenceSubCategory }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Duration</span>
                    <span class="info-value">
                      <span class="badge bg-info">{{ application.licenceDuration }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Invoice Details -->
            <div class="col-md-6">
              <div class="info-card">
                <div class="info-card-header">
                  <i class="bx bx-receipt"></i>
                  <span>Licence Invoice</span>
                </div>
                <div class="info-card-body">
                  <div class="info-row">
                    <span class="info-label">Invoice No</span>
                    <span class="info-value">
                      <a href="javascript:void(0)" @click="printBill" class="text-primary fw-semibold">
                        {{ application.licenceBillNo }}
                      </a>
                    </span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Invoice Amount</span>
                    <span class="info-value fw-semibold">{{ formatCurrency(application.licenceDetailAmount) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Amount Paid</span>
                    <span class="info-value text-success">{{ formatCurrency(application.licenceReceiptAmount) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Balance</span>
                    <span class="info-value" :class="hasBalance ? 'text-danger fw-bold' : 'text-success'">
                      {{ formatCurrency(application.licenceBillBalance) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Inspection Notes -->
            <div class="col-12" v-if="application.comments">
              <div class="info-card">
                <div class="info-card-header bg-info-subtle">
                  <i class="bx bx-notepad"></i>
                  <span>Inspection Notes</span>
                </div>
                <div class="info-card-body">
                  <div class="alert alert-soft-info mb-0">
                    <p class="mb-2">{{ application.comments }}</p>
                    <hr class="my-2">
                    <small class="text-muted fw-semibold">
                      <i class="bx bx-user me-1"></i>Inspected by: {{ application.issuingOfficer }}
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Committee Review -->
            <div class="col-12" v-if="application.minutesNumber">
              <div class="info-card">
                <div class="info-card-header bg-warning-subtle">
                  <i class="bx bx-file"></i>
                  <span>Committee Review Outcome</span>
                </div>
                <div class="info-card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="info-row">
                        <span class="info-label">Minutes Number</span>
                        <span class="info-value fw-semibold">{{ application.minutesNumber }}</span>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="info-row">
                        <span class="info-label">Minutes Document</span>
                        <span class="info-value">
                          <a :href="application.documentURL" target="_blank" class="text-primary">
                            <i class="bx bx-link-external me-1"></i>{{ application.minutesFile }}
                          </a>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Licence Status Card -->
            <div class="col-12">
              <div class="licence-card">
                <div class="licence-header">
                  <i class="bx bx-check-shield"></i>
                  <span>Licence Status</span>
                </div>
                <div class="licence-body">
                  <div v-if="application.approved === 'true'" class="alert alert-soft-success mb-0">
                    <div class="d-flex align-items-center">
                      <div class="licence-icon success me-3">
                        <i class="bx bx-certification"></i>
                      </div>
                      <div>
                        <h6 class="mb-1 fw-bold">Licence Issued & Active</h6>
                        <p class="mb-0 small">
                          This alcoholic drinks licence has been issued and is currently active. 
                          The permit can be printed for the business premises.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="alert alert-soft-warning mb-0">
                    <div class="d-flex align-items-center">
                      <div class="licence-icon warning me-3">
                        <i class="bx bx-time-five"></i>
                      </div>
                      <div>
                        <h6 class="mb-1 fw-bold">Pending Final Approval</h6>
                        <p class="mb-0 small">
                          {{ hasBalance ? 'Payment is pending before the permit can be printed.' : 'The licence is awaiting final approval.' }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light border-top">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Close
          </button>
          <button type="button" class="btn btn-info" @click="printBill">
            <i class="bx bx-printer me-1"></i>Print Invoice
          </button>
          <button 
            v-if="hasBalance"
            type="button" 
            class="btn btn-warning" 
            @click="receivePayment"
            data-bs-dismiss="modal">
            <i class="bx bx-money me-1"></i>Receive Payment
          </button>
          <button 
            v-if="application.approved === 'true'"
            type="button" 
            class="btn btn-success" 
            @click="printPermit">
            <i class="bx bx-certification me-1"></i>Print Permit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "ActiveCertModal",
  props: {
    application: {
      type: Object,
      default: () => ({})
    },
    applicationStatuses: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    hasBalance() {
      return parseFloat(this.application.licenceBillBalance) > 0;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString || dateString === '-') return '-';
      return moment(dateString).format('DD MMM YYYY');
    },

    formatCurrency(amount) {
      if (!amount) return 'KES 0';
      const num = parseFloat(amount);
      return 'KES ' + num.toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },

    getTimelineClass(item) {
      if (item.completed === 'true') return 'completed';
      if (item.completed === 'active') return 'active';
      return 'pending';
    },

    printBill() {
      this.$emit('print-bill', this.application.licenceBillNo);
    },

    printPermit() {
      this.$emit('print-permit', this.application.businessID);
    },

    receivePayment() {
      this.$emit('receive-payment', this.application.licenceBillNo);
    }
  }
}
</script>

<style scoped>
/* Modal Header */
.modal-header {
  background: linear-gradient(135deg, #0061f2 0%, #00d2ff 100%);
  color: white;
  border-radius: 0;
  padding: 1.25rem 1.5rem;
}

.modal-header-content {
  width: 100%;
}

.modal-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.btn-close-white {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.btn-close-white:hover {
  opacity: 1;
}

/* Modal Body */
.modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  padding: 1.5rem;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

/* Section Title */
.section-title {
  color: #334155;
  font-weight: 600;
  display: flex;
  align-items: center;
}

/* Timeline */
.timeline-track {
  display: flex;
  overflow-x: auto;
  padding: 10px 0;
  gap: 0;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
  position: relative;
  flex: 1;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 16px;
  left: calc(50% + 20px);
  width: calc(100% - 40px);
  height: 2px;
  background: #e2e8f0;
}

.timeline-item.completed:not(:last-child)::after {
  background: #00b894;
}

.timeline-marker {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  z-index: 1;
  background: #e2e8f0;
  color: #64748b;
}

.timeline-item.completed .timeline-marker {
  background: #00b894;
  color: white;
}

.timeline-item.active .timeline-marker {
  background: #0061f2;
  color: white;
}

.timeline-content {
  text-align: center;
  margin-top: 8px;
}

.timeline-status {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.timeline-date {
  display: block;
  font-size: 10px;
  color: #94a3b8;
}

.timeline-officer {
  display: block;
  font-size: 10px;
  color: #64748b;
  font-style: italic;
}

/* Info Cards */
.info-card {
  background: #f8fafc;
  border-radius: 10px;
  overflow: hidden;
  height: 100%;
}

.info-card-header {
  background: #e2e8f0;
  padding: 10px 15px;
  font-weight: 600;
  font-size: 13px;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-card-header i {
  font-size: 16px;
}

.info-card-header.bg-info-subtle {
  background: rgba(0, 97, 242, 0.15);
  color: #0061f2;
}

.info-card-header.bg-warning-subtle {
  background: rgba(251, 191, 36, 0.15);
  color: #d97706;
}

.info-card-body {
  padding: 12px 15px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px dashed #e2e8f0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: #64748b;
  font-size: 12px;
}

.info-value {
  color: #334155;
  font-size: 13px;
  text-align: right;
}

/* Licence Card */
.licence-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  overflow: hidden;
}

.licence-header {
  background: linear-gradient(135deg, #0061f2 0%, #00d2ff 100%);
  padding: 12px 15px;
  font-weight: 600;
  font-size: 14px;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.licence-body {
  padding: 15px;
}

.licence-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.licence-icon.success {
  background: rgba(0, 184, 148, 0.15);
  color: #00b894;
}

.licence-icon.warning {
  background: rgba(225, 112, 85, 0.15);
  color: #e17055;
}

/* Alert Soft */
.alert-soft-info {
  background: rgba(0, 97, 242, 0.1);
  border: none;
  color: #0061f2;
}

.alert-soft-success {
  background: rgba(0, 184, 148, 0.1);
  border: none;
  color: #00856d;
}

.alert-soft-warning {
  background: rgba(225, 112, 85, 0.1);
  border: none;
  color: #c0392b;
}

/* Footer */
.modal-footer {
  padding: 1rem 1.5rem;
}
</style>
